import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Megaphone, Sparkles, Users as UsersIcon, Send, Lock, LineChart, Wallet, Workflow, BarChart3, Plug, Calendar, FolderOpen, Gift, FileDown, BookUser, History as HistoryIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import SegmentsPanel from "./marketing/SegmentsPanel";
import CampaignsPanel from "./marketing/CampaignsPanel";
import GrowthDashboard from "./marketing/GrowthDashboard";
import BudgetROI from "./marketing/BudgetROI";
import AutomationsPanel from "./marketing/AutomationsPanel";
import MarketingContactsPanel from "./marketing/MarketingContactsPanel";
import MarketingHistoryPanel from "./marketing/MarketingHistoryPanel";
import {
  AiStudioPanel,
  IntelligencePanel,
  LeadCapturePanel,
  EventsPanel,
  AssetsPanel,
  ReferralsPanel,
} from "./marketing/GrowthExtras";

function ComingSoon() {
  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[900px] mx-auto" data-testid="marketing-coming-soon">
      <header className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Marketing</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Reach more buyers.</h1>
        <p className="text-sm text-muted-foreground mt-2">Targeted outreach, drip journeys and content tools — rolling out to teams soon.</p>
      </header>

      <Card className="rounded-3xl border-border/60 shadow-soft overflow-hidden">
        <CardContent className="p-10 sm:p-14 text-center">
          <div className="mx-auto h-16 w-16 rounded-3xl bg-primary/10 text-primary grid place-items-center mb-5">
            <Lock className="h-7 w-7" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-primary inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Coming soon
          </p>
          <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-3">Marketing tools are being tuned up.</h2>
          <p className="text-sm text-muted-foreground mt-3 max-w-md mx-auto">
            We're piloting segment-based WhatsApp outreach with the admin first. Once the workflow is dialled in, we'll open it up to managers and the marketing team.
          </p>
          <p className="text-xs text-muted-foreground mt-6 italic">Got a campaign idea in mind? Drop it in Feedback — we'll prioritise it.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function MarketingHub() {
  // Stream the PDF from the API (auth cookie carries through) and trigger
  // a download in the browser. Built on each call so the doc always
  // reflects the latest module surface.
  const downloadTrainingPdf = async () => {
    try {
      const res = await api.get("/marketing/training-guide.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "RENVIRO-Marketing-Growth-Engine-Training-Guide.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error("Could not download training guide");
    }
  };

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-[1280px] mx-auto space-y-6" data-testid="marketing-hub">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground inline-flex items-center gap-1.5">
            <Megaphone className="h-3 w-3" /> Marketing · Growth Engine
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Where revenue starts.</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Plan campaigns. Build audiences. Track every rupee of spend back to closed-won revenue. AI surfaces what's working and what isn't.
          </p>
        </div>
        <Button
          variant="outline"
          className="rounded-2xl tap shrink-0"
          onClick={downloadTrainingPdf}
          data-testid="marketing-training-pdf"
          title="4-page printable team training guide"
        >
          <FileDown className="h-4 w-4 mr-1.5" /> Training PDF
        </Button>
      </header>

      <Tabs defaultValue="contacts" className="space-y-4">
        <TabsList className="rounded-2xl bg-muted/60 p-1 flex-wrap h-auto gap-1 justify-start">
          <TabsTrigger value="contacts"      className="rounded-xl px-3" data-testid="tab-mkt-contacts"><BookUser className="h-4 w-4 mr-1.5" /> Marketing Contacts</TabsTrigger>
          <TabsTrigger value="growth"        className="rounded-xl px-3" data-testid="tab-growth"><LineChart className="h-4 w-4 mr-1.5" /> Growth</TabsTrigger>
          <TabsTrigger value="segments"      className="rounded-xl px-3" data-testid="tab-segments"><UsersIcon className="h-4 w-4 mr-1.5" /> Segments</TabsTrigger>
          <TabsTrigger value="campaigns"     className="rounded-xl px-3" data-testid="tab-campaigns"><Send className="h-4 w-4 mr-1.5" /> Campaigns</TabsTrigger>
          <TabsTrigger value="history"       className="rounded-xl px-3" data-testid="tab-mkt-history"><HistoryIcon className="h-4 w-4 mr-1.5" /> History</TabsTrigger>
          <TabsTrigger value="budget"        className="rounded-xl px-3" data-testid="tab-budget"><Wallet className="h-4 w-4 mr-1.5" /> Budget</TabsTrigger>
          <TabsTrigger value="automations"   className="rounded-xl px-3" data-testid="tab-automations"><Workflow className="h-4 w-4 mr-1.5" /> Automations</TabsTrigger>
          <TabsTrigger value="ai-studio"     className="rounded-xl px-3" data-testid="tab-ai-studio"><Sparkles className="h-4 w-4 mr-1.5" /> AI Studio</TabsTrigger>
          <TabsTrigger value="intelligence"  className="rounded-xl px-3" data-testid="tab-intelligence"><BarChart3 className="h-4 w-4 mr-1.5" /> Intelligence</TabsTrigger>
          <TabsTrigger value="lead-capture"  className="rounded-xl px-3" data-testid="tab-lead-capture"><Plug className="h-4 w-4 mr-1.5" /> Lead Capture</TabsTrigger>
          <TabsTrigger value="events"        className="rounded-xl px-3" data-testid="tab-events"><Calendar className="h-4 w-4 mr-1.5" /> Events</TabsTrigger>
          <TabsTrigger value="assets"        className="rounded-xl px-3" data-testid="tab-assets"><FolderOpen className="h-4 w-4 mr-1.5" /> Assets</TabsTrigger>
          <TabsTrigger value="referrals"     className="rounded-xl px-3" data-testid="tab-referrals"><Gift className="h-4 w-4 mr-1.5" /> Referrals</TabsTrigger>
        </TabsList>
        <TabsContent value="contacts"     className="mt-0"><MarketingContactsPanel /></TabsContent>
        <TabsContent value="growth"       className="mt-0"><GrowthDashboard /></TabsContent>
        <TabsContent value="segments"     className="mt-0"><SegmentsPanel /></TabsContent>
        <TabsContent value="campaigns"    className="mt-0"><CampaignsPanel /></TabsContent>
        <TabsContent value="history"      className="mt-0"><MarketingHistoryPanel /></TabsContent>
        <TabsContent value="budget"       className="mt-0"><BudgetROI /></TabsContent>
        <TabsContent value="automations"  className="mt-0"><AutomationsPanel /></TabsContent>
        <TabsContent value="ai-studio"    className="mt-0"><AiStudioPanel /></TabsContent>
        <TabsContent value="intelligence" className="mt-0"><IntelligencePanel /></TabsContent>
        <TabsContent value="lead-capture" className="mt-0"><LeadCapturePanel /></TabsContent>
        <TabsContent value="events"       className="mt-0"><EventsPanel /></TabsContent>
        <TabsContent value="assets"       className="mt-0"><AssetsPanel /></TabsContent>
        <TabsContent value="referrals"    className="mt-0"><ReferralsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

export default function Marketing() {
  const { user } = useAuth();
  // Anyone who's allowed to view marketing data sees the full Growth
  // Engine. This covers admin / manager / marketing / salesperson roles —
  // i.e. anyone whose ROLE_PERMS set includes "view_marketing". The
  // backend's per-endpoint perm checks remain the source of truth for
  // mutations (add_marketing / edit_marketing / delete_marketing).
  const can = user?.role === "admin"
    || (user?.permissions || []).includes("view_marketing");
  if (!can) return <ComingSoon />;
  return <MarketingHub />;
}
